import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Mail';
import { api, apiErrorMessage } from '../../api/client';
import { track } from '../../lib/analytics';

export default function ContactPage() {
  useEffect(() => {
    document.title = 'Contact — Baton';
    window.scrollTo({ top: 0 });
  }, []);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  function handleStart() {
    if (startedRef.current) return;
    startedRef.current = true;
    track('Contact Started');
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email.');
      track('Contact Error', { reason: 'missing_email' });
      return;
    }
    if (!message.trim()) {
      setError('Please enter a message.');
      track('Contact Error', { reason: 'missing_message' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/contact', { name, email, subject, message, website });
      setDone(true);
      track('Contact Submitted', {
        has_subject: subject.trim() ? 'yes' : 'no',
        message_length_bucket: bucketLength(message.length),
      });
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      const reason = apiErrorMessage(err, 'Could not send your message. Please try again.');
      setError(reason);
      track('Contact Error', { reason: 'api_error' });
    } finally {
      setBusy(false);
    }
  }

  function bucketLength(n: number): string {
    if (n < 100) return 'short';
    if (n < 500) return 'medium';
    return 'long';
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, md: 9 } }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
        Contact us
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1.5 }}>
        Question about Baton? Feedback from the field? Drop a note — we read every message and reply
        from a real human.
      </Typography>

      <Card
        sx={{ mt: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
        elevation={0}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {done ? (
            <Alert severity="success" icon={<EmailIcon fontSize="inherit" />}>
              Thanks — your message is on its way. We'll reply to your email shortly.
            </Alert>
          ) : (
            <form onSubmit={onSubmit} onFocus={handleStart} aria-label="Contact form">
              <Grid container spacing={1.5}>
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    inputProps={{ 'aria-label': 'Your name' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="you@yourschool.org"
                    inputProps={{ 'aria-label': 'Email address' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    inputProps={{ 'aria-label': 'Subject', maxLength: 200 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Message"
                    required
                    multiline
                    minRows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    inputProps={{ 'aria-label': 'Message', maxLength: 4000 }}
                  />
                </Grid>
                {/* Honeypot */}
                <Box
                  component="label"
                  aria-hidden
                  sx={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
                >
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </Box>
                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={1.5}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={busy}
                      aria-label="Send message"
                      sx={{ px: 3, py: 1.1 }}
                    >
                      {busy ? 'Sending…' : 'Send message'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      We'll only use your email to reply to you.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
